<template>
  <div class="console-log-list-wrapper">
    <LogSearch v-model="consoleSearch" />
    <LogFilters v-model="consoleFilterValue" :options="filterOptions" />

    <div ref="containerRef" class="console-log-list" data-testid="logs-console-list">
      <LogTable>
        <LogEntry
          v-for="entry in entries"
          :key="entry.id"
          :id="entry.id"
          :level="entry.level"
          :timestamp="entry.timestamp"
          :message="entry.message"
          :active="entry.id === activeEntryId"
          @click="$emit('seek', entry.timestamp)"
        />
      </LogTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import LogEntry from './LogEntry.vue';
import LogTable from './LogTable.vue';
import LogSearch from './LogSearch.vue';
import LogFilters, { type FilterOption } from './LogFilters.vue';
import { storeToRefs } from 'pinia';
import { useLogsStore } from '@/modules/viewer/store/useLogsStore';
import type { ConsoleFilter } from '@/modules/viewer/types/logs';
import { useActiveLogEntry } from '@/modules/viewer/composables/useActiveLogEntry';

const props = defineProps<{
  autoScroll: boolean;
}>();

defineEmits<{
  seek: [timestamp: number];
}>();

const { filteredConsoleEntries, consoleFilter, consoleSearch, consoleCounts } = storeToRefs(useLogsStore());
const entries = computed(() => filteredConsoleEntries.value);

// v-model proxy for filter so the template uses a string; the store exposes the ref directly.
const consoleFilterValue = computed<ConsoleFilter>({
  get: () => consoleFilter.value,
  set: value => {
    consoleFilter.value = value as ConsoleFilter;
  },
});

const filterOptions = computed<FilterOption[]>(() => [
  { value: 'all', label: 'All', count: consoleCounts.value.all },
  { value: 'error', label: 'Error', count: consoleCounts.value.error },
  { value: 'warn', label: 'Warn', count: consoleCounts.value.warn },
  { value: 'info', label: 'Info', count: consoleCounts.value.info },
  { value: 'debug', label: 'Debug', count: consoleCounts.value.debug },
]);

const { containerRef, activeEntryId } = useActiveLogEntry(entries, toRef(props, 'autoScroll'));
</script>

<style scoped>
.console-log-list-wrapper {
  @apply flex flex-col;
}

.console-log-list {
  @apply flex flex-col px-4 sm:px-5 pt-4 pb-4;
}
</style>
